
import { permissionAllowed, SystemOperate, SystemPlanStyle, SystemRTM } from "@ews/react-data";
import Icon, { IconName } from "@ews/react-icons";
import { useL10n } from "@ews/react-localization-context";

import
{
    AuthorizationLevel,
    NoDeviceNumber,
    NumberGroup,
    PushCategory,
    RTMCategory,
    providesMeasurements as _providesMeasurements,
    eventDetails,
    eventGetSymbol,
    eventPushCategory,
    formatLogicalAddress,
    isValidDeviceNumber
} from "@ews/zlt-events";

import { Command, getTranslationKey } from "@ews/zlt-operate";
import { IonButton, IonIcon, IonInput, IonItem, IonLabel, IonList, IonSelect, IonSelectOption, useIonRouter } from "@ionic/react";
import { arrowBack, arrowDown, trash } from "ionicons/icons";
import { LatLng, Icon as Licon, Marker as MarkerType, Tooltip } from "leaflet";
import React, { useEffect, useRef, useState } from "react";
import { Marker, Tooltip as MarkerTooltip, Popup } from 'react-leaflet';
import { floatingPromiseHandler } from "../../../../utils";
import { useAuthorization } from "../../../Authorization";
import { useMyPermissions, useMyProfile } from "../../../Login/LoginProvider";
import { useNamedRoutes } from "../../../NamedRoutes";
import { getAddress, getNumberGroup, getSystemId, validOperations } from "../../../Navigation/Sidebar/Operate";
import { usePersistentState } from "../../../Persistence";
import { SystemElement, SystemGroup, useOperateSystem } from "../../../ReactData/system";
import SettingsSelectOption from "../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption";
import { blink, blinkLC600 } from "../../blink";
import { lookupIconCode } from "../../iconMap";
import style from "./Element.module.scss";
import { Alignment, AlignmentsTranslationKey, ElementProps, alignments, fontFamilies, fontWeights, labelPosition } from "./types";

const defaultFontSize = 6;
const defaultIconSize = 24;
//const defaultIconSize = 12;
const maxPushCategoryValue = Object.values(PushCategory).filter(v => !isNaN(Number(v))).reduce((a, c) => Number(a) | Number(c), 0);

export function getDeviceIconsFilePath(file: string, folder: string = "device-icons")
{
    return `assets/${folder}/${file}.png`;
}

function convertStringToRoundedNumber(value?: string | number)
{
    return Number(Number(value || 0).toFixed(0));
}

export function getScaleSize(originSize: number = defaultFontSize, zoom: number = 0,)
{
    let result = Math.pow(2, zoom) * originSize;
    return result;
}

function getFontFamilyClass(fontFamily: string = "Times New Roman")
{
    return fontFamilies.find(font => font.label === fontFamily)?.class;
}
function getLabelBackground(value: boolean | null = null, defaultValue: boolean | null = null)
{
    if (value === null || value === undefined) {
        return defaultValue && (defaultValue !== null || defaultValue !== undefined);
    }
    return value && (value !== null || value !== undefined);
}

export function getIconScaleSize(scale: number, iconScale: number = 1)
{
    return scale * Number(iconScale);  //Number(iconScale) >= 0 ? scale * Math.abs(Number(iconScale)) : scale / Math.abs(Number(iconScale));
}

function getLabelOffset(alignment: Alignment = "bottom", iconSize: number = defaultIconSize): [number, number]
{

    const offset = iconSize / 2;
    let size: [number, number] = [0, 0]; // [width, height]

    /*
        if (+iconScale === 2) {
            scale = -30;
        } else if (+iconScale === 1) {
            scale = -10;
        } else if (+iconScale === -2) {
            scale = 0;
        } else if (+iconScale === -4) {
            scale = 5;
        } */

    if (alignment === "bottom") {
        size[1] = offset;
    }
    else if (alignment === "top") {
        size[1] = -offset;
    }
    else if (alignment === "left") {
        size[0] = -offset;
    }
    else if (alignment === "right") {
        size[0] = offset;
    }

    return size;
    //return [0, 0]; //size;
}

function getPosition(elements: SystemElement[], alignment: Alignment)
{
    if (alignment === "bottom") {
        const numbers = elements.map(element => Number(element.systemPlanX || 0));
        return { systemPlanX: Math.min(...numbers) };
    }
    else if (alignment === "top") {
        const numbers = elements.map(element => Number(element.systemPlanX || 0));
        return { systemPlanX: Math.max(...numbers) };
    }
    else if (alignment === "left") {
        const numbers = elements.map(element => element.systemPlanY || 0);
        return { systemPlanY: Math.min(...numbers) };
    }
    else if (alignment === "right") {
        const numbers = elements.map(element => element.systemPlanY || 0);
        return { systemPlanY: Math.max(...numbers) };
    }
    return null;
}
export function getMarkerId(element: SystemElement)
{
    return `marker${element.groupId}_${element.id}_${element.numberGroup}`;
}

const T_ELEM = "TD_ELEM";
const T_GROUP_LIST: Partial<Record<NumberGroup, string>> = {
    [NumberGroup.Zone]: "[SID_GROUP_DEV_SHORT]",
    [NumberGroup.ActuationDevice]: "[SID_CONTR_DEV_SHORT]",
    [NumberGroup.TransmissionDevice]: "[SID_TRANS_DEV_SHORT]",
    [NumberGroup.AlarmingDevice]: "[SID_AL_DEV_SHORT]",
    [NumberGroup.FloodingZone]: "[SID_EXT_SEC_DEV_SH]"
};

const hasValidTestAlarm = (element: SystemElement, group: SystemGroup): boolean =>
{
    const { eventKindType } = group;
    const { rtm } = eventDetails(eventKindType!) || {};

    if (rtm === RTMCategory.TestCondition) {

        const testRTM = group.rtm?.filter(e => e.eventKindType === eventKindType).pop();
        if (testRTM && element.latestTestAlarm > testRTM.timestamp) return true;
    }

    return false;
};

const getEventIcon = (element: SystemElement, group: SystemGroup): IconName =>
{
    const address = getAddress(element);
    const { eventKindType } = element;
    if (hasValidTestAlarm(element, group)) return "Test_alarm_L_red";

    return eventGetSymbol(eventKindType!, address);

};

const getIconCode = (element: SystemElement, group: SystemGroup): number =>
{
    const { eventKindType } = element;
    if (hasValidTestAlarm(element, group)) return 11;

    return lookupIconCode(eventKindType!);
};



export const PlanLabelTemplates: React.FC<{}> = () =>
{
    const { translate: t } = useL10n();

    const labelTemplate: { label: string, value: string; }[] = [
        { label: "---", value: "" },
        { label: "xxx/00y", value: "%{logicalAddress}" },
        { label: "xxx/y", value: "%{logicalAddressWithoutLeadingZero}" },
        { label: `xxx/00y, ${t("[SID_DLG_TXT_TEXT1]")} ${t("TD_ELEMENTTEXT")}`, value: "%{logicalAddress}, %{groupText} %{elementText}" }
    ];

    return <>{
        labelTemplate.map((template, index) => <SettingsSelectOption key={index} text={template.label} value={template.value} />)
    }</>;
};

const providesMeasurements = (target: SystemGroup | SystemElement) =>
{
    if ("elements" in target) {
        return Boolean(target.elements?.filter(e => _providesMeasurements(e.additionalParameters.AddrType || 'Unknown')).length);
    }
    return _providesMeasurements(target.additionalParameters.AddrType || 'Unknown');
};

const Element: React.FC<ElementProps> = ({
    element,
    group,
    selectedElements,
    onAlignment,
    onSelect,
    onChange,
    onDelete,
    onChangeTooltipDirection,
    zoom,
    selected = false,
    planHeight = 0,
    planWidth = 0,
    defaultStyle = {},
    allowedFlashEvents = 0,
    hasFloodingZone,
    showLabel,
    mapWidth,
    mapHeight
}) =>
{


    function iconAllowedToFlash(eventKindType?: number, userSettings: number = 0)
    {
        if (!eventKindType) return false;

        const eventCategory = eventPushCategory(eventKindType);
        const iconCode = getIconCode(element, group);
        const blinkDependency = hasFloodingZone ? blinkLC600[iconCode] : blink[iconCode];

        if (!blinkDependency) {
            return false;
        }
        else if ((maxPushCategoryValue & blinkDependency.category) === maxPushCategoryValue) {
            return true;
        }

        const hasUserPermissionToBlink = (userSettings & blinkDependency.category) === eventCategory;

        return hasUserPermissionToBlink;
    }

    function getToolTipId()
    {
        return (tooltipRef.current as Tooltip & { _leaflet_id: number; })?._leaflet_id;
    }

    const { translate: t } = useL10n();
    const permissions = useMyPermissions();
    const { authorizationLevel } = useAuthorization();
    const { generate } = useNamedRoutes();
    const router = useIonRouter();
    const [planEdit] = usePersistentState<boolean>("authorizationLevelPlan");

    const operationsGroup = validOperations(authorizationLevel, group);
    const operationsElement = isValidDeviceNumber(element.id || 0) ? validOperations(authorizationLevel, element) : [];
    const me = useMyProfile();

    const operate = useOperateSystem();

    const T_GROUP = T_GROUP_LIST[(group.numberGroup || NumberGroup.Zone) as keyof typeof T_GROUP_LIST] || "";

    const handleOperate = async (command: string, operateGroup: boolean = false) =>
    {
        const from = getAddress(operateGroup ? group : element);
        const numberGroup = getNumberGroup(operateGroup ? group : element);
        const systemId = getSystemId(operateGroup ? group : element);

        await operate(systemId!, {
            command,
            numberGroup,
            firstZone: from.zone,
            lastZone: from.zone,
            firstElement: from.element,
            lastElement: from.element,
        } as SystemOperate);
    };


    let fontStyle = { ...defaultStyle, ...element?.systemPlanStyle };
    //const labelBackground = getLabelBackground(fontStyle?.labelBackground, false);

    function setStyle(value: any, type: keyof SystemPlanStyle)
    {

        if (!("systemPlanStyle" in element)) {
            element.systemPlanStyle = {};
        }
        element.systemPlanStyle![type] = value;

        fontStyle = { ...element.systemPlanStyle };
    }

    const markerRef = useRef<MarkerType>(null);
    const xRef = useRef<HTMLIonInputElement>(null);
    const yRef = useRef<HTMLIonInputElement>(null);
    const tooltipRef = useRef<Tooltip>(null);

    function resetValue(ref: React.RefObject<HTMLIonInputElement>, value: string | number)
    {
        if (ref.current) ref.current.value = value;

    }


    function checkImage(
        path: string,
        onload: (path: string) => void,
        onerror: (e: string | Event) => void
    )
    {
        const img = new Image();

        img.id = getMarkerId(element);
        //img.loading = "lazy";
        img.alt = "";
        img.height = 0;
        img.width = 0;

        const domElement = document.querySelector("#" + img.id);
        if (!domElement) {
            document.body.append(img);
        }

        img.onload = () =>
        {
            img.hidden = true;
            onload(path);
        };
        img.onerror = (e) =>
        {
            onerror(e);
            if (domElement) {
                domElement.remove();
            }
        };
        img.src = path;
    }

    const { systemPlanX, systemPlanY, systemPlanIcon, text, groupId, id, rtm, eventKindType, numberGroup } = element;

    const logicalAddress = `${formatLogicalAddress(groupId!, id || NoDeviceNumber, numberGroup || NumberGroup.Zone, true)}`;

    const logicalAddressWithoutLeadingZero = `${formatLogicalAddress(groupId!, id || NoDeviceNumber, numberGroup || NumberGroup.Zone, false)}`;

    const scale = getScaleSize(defaultIconSize, zoom || 0);

    const iconScale = fontStyle?.iconScale || defaultStyle.iconScale;
    const iconSize = getIconScaleSize(scale, iconScale);

    const [icon, setIcon] = useState<Licon | null>(null);
    const [alignment, setAlignment] = useState<Alignment | null>(null);

    function getClassName(selected: boolean, eventKindType?: number,)
    {
        const allowedToFlash = iconAllowedToFlash(eventKindType, allowedFlashEvents);

        let className = `${allowedToFlash ? style.blink : ""}`;
        // if ((deviceList[systemPlanIcon as keyof Devices]?.[eventKindType || 0])) {
        //     const { blink } = deviceList[systemPlanIcon as keyof Devices][eventKindType || 0];
        //     className = `${blink && allowedToFlash ? style.blink : ""}`;
        //}
        if (selected) {
            className += ` ${style.selected}`;
        }
        return className;
    };

    function _setIcon(path: string,)
    {
        const icon = new Licon({
            className: "",
            iconUrl: getDeviceIconsFilePath("question"),
            iconSize: [iconSize, iconSize]
        });

        checkImage(path, (_path) =>
        {
            icon.options.iconUrl = _path;

            icon.options.className = getClassName(selected, eventKindType);
            setIcon(new Licon({ ...icon.options }));
        },
            (error) =>
            {
                icon.options.iconUrl = getDeviceIconsFilePath("question");
                icon.options.className = getClassName(selected, eventKindType);
                setIcon(new Licon({ ...icon.options }));
            }
        );
    }

    useEffect(() =>
    {
        const direction = fontStyle.labelPosition || defaultStyle.labelPosition || "left";
        if (onChangeTooltipDirection) {
            const tooltipId = getToolTipId();
            onChangeTooltipDirection({ direction, tooltipId });
        }
    }, [defaultStyle, element]);

    useEffect(() =>
    {
        if (systemPlanIcon) {

            const path = getDeviceIconsFilePath(`${""}${systemPlanIcon}_${getIconCode(element, group)}`);
            _setIcon(path);
        }
        return () => { };
    }, [eventKindType, zoom, selected, systemPlanIcon, showLabel, allowedFlashEvents]);

    if (
        icon && icon.options.iconUrl !== getDeviceIconsFilePath("question") &&
        getDeviceIconsFilePath(`${""}${systemPlanIcon}_${getIconCode(element, group)}`) !== icon?.options.iconUrl
    ) {
        icon.options.iconUrl = getDeviceIconsFilePath(`${""}${systemPlanIcon}_${getIconCode(element, group)}`);
        icon.options.className = getClassName(selected, eventKindType);
        setIcon(new Licon({ ...icon.options }));
    };
    if (icon && iconSize !== (icon?.options.iconSize as [number, number])[0]) {
        setIcon(new Licon({ ...icon.options, iconSize: [iconSize, iconSize] }));
    }
    const elementLabel = (element.systemPlanStyle?.label)?.
        replace("%{elementText}", element.text || "").
        replace("%{groupText}", group.text || "").
        replace("%{logicalAddress}", logicalAddress).
        replace("%{logicalAddressWithoutLeadingZero}", logicalAddressWithoutLeadingZero);

    return !icon ? null : <>
        <Marker
            interactive={true}
            ref={markerRef}
            eventHandlers={{
                contextmenu: (e) =>
                {
                    if (onSelect) onSelect(element);
                },
                dragend: (e) =>
                {
                    const position: LatLng = (e.target as any).getLatLng();
                    element.systemPlanX = position.lat;
                    element.systemPlanY = position.lng;

                    if (onChange) onChange(element, { ...element });
                }

            }}
            draggable={planEdit}
            position={[systemPlanX!, systemPlanY!]}
            icon={icon}
        >
            <Popup>
                <section
                    className={`${style.popover}`}>
                    {selected && selectedElements.length ?
                        <>
                            <IonItem lines="full">
                                <IonSelect
                                    value={alignment}
                                    interface="popover"
                                    label={t("[REACT_ALIGNMENT]")}
                                    onIonChange={({ detail }) => setAlignment(detail.value)
                                    }
                                >
                                    {alignments.map(alignment =>
                                        <IonSelectOption
                                            key={alignment}
                                            value={alignment}
                                        >
                                            {t(AlignmentsTranslationKey[`${alignment}`])}
                                        </IonSelectOption>
                                    )}
                                </IonSelect>
                            </IonItem>
                            <IonItem lines="full">
                                <div
                                    className={style.buttonContainer}
                                >
                                    <IonButton
                                        disabled={alignment === null}
                                        color={alignment !== null ? "warning" : "medium"}
                                        expand='full'
                                        onClick={() =>
                                        {
                                            if (!alignment) return;
                                            const position = getPosition(selectedElements, alignment);

                                            if (!position) return;

                                            const elements = selectedElements.map(element =>
                                            {
                                                return { ...element, ...position } as SystemElement;
                                            });

                                            if (onAlignment) onAlignment(elements);
                                        }}>
                                        {t("[REACT_ALIGNMENT]")}
                                    </IonButton>
                                </div>
                            </IonItem>
                        </>
                        : <>
                            <IonItem lines="full">
                                <div slot="start">{t(T_GROUP)}:</div>
                                <div slot="end">{logicalAddress}</div>
                            </IonItem>

                            <IonItem lines="full">
                                <div slot="start">{t(T_GROUP)}{"-Text"}:</div>
                                <div slot="end">{group.text}</div>
                            </IonItem>


                            {isValidDeviceNumber(element.id || 0) && <IonItem lines="full">
                                <div slot="start">{t("[REACT_ELM_TEXT]")}:</div>
                                <IonInput
                                    slot="end"
                                    disabled={!planEdit}
                                    label=''
                                    className={style.input}
                                    value={element.text}
                                    onIonInput={({ detail }) =>
                                    {
                                        element.text = detail.value || "";
                                    }} />

                            </IonItem>}

                            {planEdit ?
                                <IonItem lines="full">
                                    <div slot="start">{t("[REACT_TEMPLATE]")}:</div>
                                    <IonSelect
                                        slot="end"
                                        label={""}
                                        value={element.systemPlanStyle?.label}
                                        onIonChange={({ detail }) =>
                                        {
                                            setStyle(detail.value, "label");
                                        }}
                                    >
                                        <PlanLabelTemplates />
                                        {/*   <IonSelectOption value={"%{logicalAddress}"} >
                                            xxx/00y
                                        </IonSelectOption>
                                        <IonSelectOption value={"%{logicalAddressWithoutLeadingZero}"} >
                                            xxx/y
                                        </IonSelectOption>
                                        <IonSelectOption value={'%{logicalAddress}, %{groupText} %{elementText}'}>
                                            {`xxx/00y, ${t("[SID_DLG_TXT_TEXT1]")} ${t("TD_ELEMENTTEXT")}`}
                                        </IonSelectOption> */}
                                    </IonSelect>
                                </IonItem>
                                :
                                null
                            }
                            {
                                rtm && rtm.length ? <IonItem lines="full">
                                    <div className={style.center}>{
                                        rtm?.map((event: SystemRTM) =>
                                        {
                                            //const { zone, element, eventKindType } = event;
                                            const icon = getEventIcon(element, group); // eventGetSymbol(eventKindType, { zone, element });
                                            return <Icon icon={icon} key={`${event.zone
                                                } /${event.element}`}></Icon >;
                                        })
                                    }</div >
                                </IonItem >
                                    : null
                            }
                            {(planEdit) ?
                                <>

                                    <IonItem lines="full" className={style.noItemPadding}>
                                        <section className={style.positionContainer}>
                                            <section>
                                                <div>{t("Y")}:</div>
                                                <IonIcon icon={arrowBack} />
                                                <IonInput
                                                    slot="end"
                                                    label=''
                                                    type='number'
                                                    ref={yRef}
                                                    min={0}
                                                    max={planWidth}
                                                    value={convertStringToRoundedNumber(element.systemPlanY)}
                                                    onIonInput={({ detail }) =>
                                                    {
                                                        if (!detail.value) return;

                                                        const value = Number(detail.value);

                                                        if (value < 0 || (mapWidth && value > mapWidth)) {
                                                            resetValue(yRef, convertStringToRoundedNumber(element.systemPlanY));
                                                            return;
                                                        }

                                                        element.systemPlanY = Number(detail.value);
                                                    }} />

                                            </section>
                                            {/*    </IonItem>
                                    <IonItem lines="full"> */}
                                            <section>
                                                <div>{t("X")}:</div>
                                                <IonIcon icon={arrowDown} />
                                                <IonInput
                                                    slot="end"
                                                    label=''
                                                    type='number'
                                                    min={0}
                                                    ref={xRef}
                                                    max={planHeight}
                                                    value={convertStringToRoundedNumber(element.systemPlanX)}
                                                    onIonInput={({ detail }) =>
                                                    {

                                                        if (!detail.value) return;

                                                        const value = Number(detail.value);

                                                        if (value < 0 || (mapHeight && value > mapHeight)) {
                                                            resetValue(xRef, convertStringToRoundedNumber(element.systemPlanX));
                                                            return;
                                                        }

                                                        element.systemPlanX = Number(detail.value);
                                                    }} />

                                            </section>
                                        </section>
                                    </IonItem>

                                    <IonItem lines="full">

                                        <div slot="start">{t("[REACT_ICON_SCALE]")} ({t("[REACT_FACTOR]")}):</div>
                                        <IonInput
                                            slot="end"
                                            label=''
                                            type='number'
                                            step="0.01"
                                            min={0.01}
                                            className={style.input}
                                            value={fontStyle?.iconScale}
                                            onIonInput={({ detail }) =>
                                            {

                                                if (!detail.value) return;
                                                const value = Number(detail.value);
                                                setStyle(value !== 0 ? value : 1, "iconScale");
                                            }} />
                                        {/*
                                            <IonSelect

                                                value={`${fontStyle?.iconScale || defaultStyle?.iconScale || 1}`}
                                                interface="popover"
                                                label={t("[REACT_ICON_SCALE]")}
                                                onIonChange={({ detail }) =>
                                                {
                                                    if (!detail.value) return;
                                                    const value = Number(detail.value);
                                                    setStyle(value !== 0 ? value : 1, "iconScale");
                                                }}
                                            >
                                                <IonSelectOption value='-4' >{t('ICON_SIZE_QUARTER')}</IonSelectOption>
                                                <IonSelectOption value='-2' >{t('ICON_SIZE_HALF')}</IonSelectOption>
                                                <IonSelectOption value='1' >{t('ICON_SIZE_FULL')}</IonSelectOption>
                                                <IonSelectOption value='2' >{t('ICON_SIZE_DOUBLE')}</IonSelectOption>
                                            </IonSelect> */}

                                    </IonItem>

                                    <IonItem lines="full">
                                        <div slot="start">{t("TPL_BEEZ3_FONTSIZE")} (pt): </div>

                                        <IonInput
                                            slot="end"
                                            type='number'
                                            className={style.input}
                                            value={Number(fontStyle?.fontSize!)}
                                            onIonInput={({ detail }) =>
                                            {
                                                if (!detail.value) return;
                                                const value = Number(detail.value);
                                                setStyle(value !== 0 ? value : 1, "fontSize");
                                            }} />

                                    </IonItem>

                                    <IonItem lines="full">
                                        <IonSelect
                                            value={fontStyle?.labelPosition || "bottom"}
                                            interface="popover"
                                            label={`${t("MOD_FINDER_FIELDSET_ADVANCED_LABEL_POS_LABEL")}:`}
                                            onIonChange={({ detail }) =>
                                            {
                                                if (!detail.value) return;
                                                const direction = detail.value;
                                                setStyle(direction, "labelPosition");
                                                //setDirection(direction);

                                                if (onChangeTooltipDirection) {
                                                    const tooltipId = getToolTipId();
                                                    onChangeTooltipDirection({ direction, tooltipId });
                                                }
                                            }
                                            }
                                        >
                                            {labelPosition.map(position =>
                                                <IonSelectOption
                                                    className={style[position as string]}
                                                    key={position}
                                                    value={position}
                                                >
                                                    {t(position as string)}
                                                </IonSelectOption>
                                            )}
                                        </IonSelect>
                                    </IonItem>
                                    {/*
                                        <IonItem lines="full">
                                            <IonSelect
                                                value={labelBackground ? "show" : "hide"}
                                                interface="popover"
                                                label={t("[REACT_LABEL_BACKGROUND]")}
                                                onIonChange={({ detail }) =>
                                                {
                                                    if (!detail.value) return;
                                                    const value = detail.value === "show" ? true : false;
                                                    setStyle(value, "labelBackground");

                                                }
                                                }
                                            >
                                                {availableLabelBackground.map(_class =>
                                                    <IonSelectOption
                                                        key={_class}
                                                        value={_class}
                                                    >
                                                        {t(_class as string)}
                                                    </IonSelectOption>
                                                )}
                                            </IonSelect>

                                        </IonItem>
                                        */}
                                    <IonItem lines="full">
                                        <IonSelect
                                            value={fontStyle?.fontFamily}
                                            interface="popover"
                                            label={`${t("[REACT_FONT_FAMILY]")}:`}
                                            style={{ fontFamily: fontStyle?.fontFamily }}
                                            onIonChange={({ detail }) =>
                                            {
                                                if (!detail.value) return;
                                                setStyle(detail.value, "fontFamily");
                                            }
                                            }
                                        >
                                            {fontFamilies.map(font =>
                                                <IonSelectOption
                                                    className={style[font.class]}
                                                    key={font.class}
                                                    value={font.class}
                                                >
                                                    {t(font.label as string)}
                                                </IonSelectOption>
                                            )}
                                        </IonSelect>
                                    </IonItem>

                                    <IonItem lines="full">
                                        <IonSelect
                                            value={fontStyle?.fontWeight}
                                            interface="popover"
                                            label={`${t("[REACT_FONT_WEIGHT]")}:`}
                                            style={{
                                                fontWeight: fontStyle?.fontWeight
                                            }}
                                            onIonChange={({ detail }) =>
                                            {
                                                if (!detail.value) return;
                                                setStyle(detail.value, "fontWeight");
                                            }
                                            }
                                        >
                                            {fontWeights.map(font =>
                                                <IonSelectOption
                                                    className={style[font as string]}
                                                    style={{ fontWeights: font }}
                                                    key={font}
                                                    value={font}
                                                >
                                                    {t(font as string)}
                                                </IonSelectOption>
                                            )}
                                        </IonSelect>
                                    </IonItem>
                                    <IonItem lines="full">
                                        <div
                                            className={style.buttonContainer}
                                        >
                                            <IonButton
                                                className={style.saveBtn}
                                                color={"success"}
                                                onClick={() =>
                                                {
                                                    if (onChange) onChange(element, { ...element });
                                                }}>
                                                <IonLabel>{t("COM_FACPMANAGEMENT_USER_SAVE")}</IonLabel>
                                            </IonButton>
                                            <IonButton
                                                className={style.deleteBtn}
                                                color={"danger"}
                                                onClick={() =>
                                                {
                                                    if (onDelete) onDelete(element);
                                                }}>
                                                <IonIcon icon={trash} />
                                            </IonButton>

                                        </div>
                                    </IonItem>


                                </>
                                :
                                <>
                                    {(permissionAllowed(permissions.permitAccessLevel3!) && providesMeasurements(element)) && <IonItem>
                                        <IonButton
                                            className={style.btn}
                                            onClick={() =>
                                            {
                                                const { id, systemId, numberGroup, groupId } = element;
                                                if (
                                                    !systemId ||
                                                    (id === undefined || id === null) ||
                                                    (numberGroup === undefined || numberGroup === null)
                                                ) return;

                                                const link = generate("system:analoguevalues", { id: systemId, numberGroup: `${numberGroup}`, element: `${id}`, group: `${groupId}` });
                                                router.push(link);
                                            }}>{`${t("[REACT_LOAD_MEASUREMENTS]")}`}</IonButton>
                                    </IonItem>}

                                    {authorizationLevel > AuthorizationLevel.LEVEL1 &&
                                        <>

                                            <IonList>
                                                {operationsGroup.map((operation, i) => <IonItem key={operation} lines={i === operationsGroup.length - 1 ? "full" : "none"}>
                                                    <div
                                                        className={style.buttonContainer}
                                                    >
                                                        <IonButton
                                                            className={style.btn}
                                                            expand="full"
                                                            color={"secondary"}
                                                            onClick={() =>
                                                            {
                                                                handleOperate(operation, true).catch(floatingPromiseHandler(import.meta.url));
                                                            }}
                                                        >{t(T_GROUP)} {t(getTranslationKey(operation as Command))}</IonButton>
                                                    </div>
                                                </IonItem>)}
                                            </IonList>

                                            <IonList>
                                                {operationsElement.map((operation) => <IonItem key={operation} lines="none">
                                                    <div
                                                        className={style.buttonContainer}
                                                    >
                                                        <IonButton
                                                            className={style.btn}
                                                            expand="full"
                                                            color={"secondary"}
                                                            onClick={() =>
                                                            {
                                                                handleOperate(operation).catch(floatingPromiseHandler(import.meta.url));
                                                            }}
                                                        >{t(T_ELEM)} {t(getTranslationKey(operation as Command))}</IonButton>
                                                    </div>
                                                </IonItem>)}
                                            </IonList>
                                        </>
                                    }
                                </>
                            }

                        </>}
                </section >
            </Popup >
            {
                showLabel ?
                    <MarkerTooltip
                        ref={tooltipRef}
                        //offset={getLabelOffset(fontStyle?.labelPosition || defaultStyle.labelPosition, iconSize)}
                        offset={[0, 0]}
                        direction={fontStyle?.labelPosition || "left"}
                        interactive={false}
                        permanent
                        className={`${style.tooltip}`}

                    //className={`${style.tooltip} ${fontStyle?.showLabel ? style.show : ""}`}
                    >
                        <span
                            style={{
                                display: "block",
                                margin: `${iconSize / 2}px`,
                                fontFamily: fontStyle?.fontFamily,
                                fontWeight: fontStyle?.fontWeight || "normal",
                                fontSize: `${getScaleSize(fontStyle?.fontSize, zoom)}pt`,
                            }}
                        >
                            {elementLabel}
                        </span>
                    </MarkerTooltip> : null}
        </Marker >
    </>;
};

export default Element;
